import { useEffect, useRef, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { bkkTodayISO } from './format.js'

/**
 * <FilterCard> — the "ค้นหา / กรอง" box above a list. Audit: filters sat in a
 * bare row, inside a card, in the card's header, or next to the main button;
 * the clear button was "ล้าง", "ล้างตัวกรอง" or absent.
 *
 * Children are the filter controls (Field + Input/Select/SearchInput), laid out
 * in a wrapping row. `onClear` shows "ล้างตัวกรอง" in the header — enabled only
 * when something differs from the defaults (`canClear`). `aside` = right-aligned
 * extras in the header (e.g. a refresh button, a result count).
 */
/*
 * sticky   pins the card under the page's sticky header while the list scrolls
 *          (top offset = the CSS var --ph that PatientLayout measures, else 0)
 * compact  no title row — controls only, for a filter strip inside a page section;
 *          ล้างตัวกรอง and `aside` move to the end of the control row
 */
export function FilterCard({ title = 'ค้นหา / กรอง', onClear, canClear = true, aside, sticky = false, compact = false, children, className = '' }) {
  const clear = onClear && (
    <button type="button" onClick={onClear} disabled={!canClear}
      className="inline-flex items-center gap-1 text-[12.5px] font-medium text-slate-500 hover:text-brand-700 disabled:opacity-40 disabled:pointer-events-none">
      <RotateCcw size={12} />ล้างตัวกรอง
    </button>
  )
  const pin = sticky ? 'sticky top-[var(--ph,0px)] z-20 shadow-sm' : ''
  if (compact) {
    return (
      <section className={`rounded-2xl border border-slate-200 bg-white mb-4 ${pin} ${className}`}>
        <div className="flex flex-wrap items-end gap-3 px-4 py-3">
          {children}
          {(aside || clear) && <div className="ml-auto flex items-center gap-2 self-center">{aside}{clear}</div>}
        </div>
      </section>
    )
  }
  return (
    <section className={`rounded-2xl border border-slate-200 bg-white mb-4 ${pin} ${className}`}>
      <div className="flex items-center justify-between gap-3 px-4 pt-3">
        <h3 className="text-[13px] font-semibold text-slate-700">{title}</h3>
        <div className="flex items-center gap-2">
          {aside}
          {onClear && (
            <button type="button" onClick={onClear} disabled={!canClear}
              className="inline-flex items-center gap-1 text-[12.5px] font-medium text-slate-500 hover:text-brand-700 disabled:opacity-40 disabled:pointer-events-none">
              <RotateCcw size={12} />ล้างตัวกรอง
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 px-4 pt-2.5 pb-4">{children}</div>
    </section>
  )
}

/**
 * useFilters — page filters that are REMEMBERED (localStorage), with dates that
 * reset each new Bangkok day. Audit: some pages forgot everything on navigation,
 * others remembered "yesterday" forever and opened on a stale day the next
 * morning.
 *
 *   const { filters, set, reset, isDefault } = useFilters('lab', DEFAULTS, { daily: ['day'] })
 *   set('branch', 8) · set({ branch: 8, q: '' }) · reset()
 *
 * `daily` = keys that go back to their default when the Bangkok date changes.
 * Never put patient data in a filter key — this is browser storage.
 */
export function useFilters(key, defaults, { daily = [] } = {}) {
  const storageKey = `kit:filters:${key}`
  const [state, setState] = useState(() => {
    const base = { ...defaults }
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) return base
      const saved = JSON.parse(raw)
      const sameDay = saved?.day === bkkTodayISO()
      const values = { ...base, ...(saved?.values || {}) }
      if (!sameDay) for (const k of daily) values[k] = defaults[k]
      return values
    } catch { return base }
  })
  const defaultsRef = useRef(defaults)
  defaultsRef.current = defaults

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify({ day: bkkTodayISO(), values: state })) } catch { /* private mode */ }
  }, [storageKey, state])

  const set = (k, v) => setState((s) => (typeof k === 'object' ? { ...s, ...k } : { ...s, [k]: v }))
  const reset = () => setState({ ...defaultsRef.current })
  // JSON, not String(): a date range is an object, and String({…}) is always
  // "[object Object]" — every range would look like the default.
  const same = (a, b) => JSON.stringify(a ?? '') === JSON.stringify(b ?? '')
  const isDefault = Object.keys(defaultsRef.current).every((k) => same(state[k], defaultsRef.current[k]))
  return { filters: state, set, reset, isDefault }
}
